import { isCover, isNatural, isSolid, outwardTurn, pieceAnchor, pieceOf, tileAt, turnAt, type Grid } from "@dyson/shared";

/** Quarter turns 0..3 as unit directions: up, left, down, right. */
const SIDES = [
  [0, 1],
  [-1, 0],
  [0, -1],
  [1, 0],
] as const;

/**
 * Quarter turns (counter-clockwise) for the texture of tile (x, y). On a rock
 * that pulls (`pulls`, see GRAVITY) up is the grid direction nearest to out
 * from the centre, and every tile, built or natural, turns to it: grain,
 * rivets and lit rims face the local up. On a zero-g rock natural tiles turn
 * to face out (a planet look) and anything built stays upright to the world,
 * which is the player's up there. Ground cover (grass, moss, frost, bark)
 * points at whichever open side is closest to outward, so its top band is
 * always on the exposed edge.
 */
export function facing(g: Grid, x: number, y: number, t: number, pulls: boolean): number {
  if (!pulls && !isNatural(t)) return 0;
  // A piece is drawn the way it was put down: every part turned as its anchor is (see turnAt).
  if (pulls && pieceOf(t)) {
    const anchor = pieceAnchor(g, y * g.w + x);
    if (anchor !== null) return turnAt(g, anchor % g.w, Math.floor(anchor / g.w));
  }
  const out = outwardTurn(g, x, y);
  if (!isCover(t)) return out;
  const dx = x + 0.5 - g.w / 2;
  const dy = y + 0.5 - g.h / 2;
  let best = -1;
  let bestDot = -Infinity;
  const len = Math.hypot(dx, dy) || 1;
  SIDES.forEach(([sx, sy], q) => {
    if (isSolid(tileAt(g, x + sx, y + sy))) return;
    const dot = (sx * dx + sy * dy) / len;
    if (dot > bestDot) [best, bestDot] = [q, dot];
  });
  return best >= 0 ? best : out;
}
